/**
 * 创作描述卡片 —— 自适应高度 textarea + AI 整理 + 字数计数。
 *
 * ═══ 2026-07-30:优化前后**同一个框** ═════════════════════════════════════════
 * 改之前是两个组件互斥渲染(`PromptPanel` ↔ `OptimizedContentSection`):点一下
 * 「AI 优化」,框换了、按钮换了、连卡片标题都换了 —— 用户以为自己进了另一个页面,
 * 而他做的只是「让我们把他那段话整理一下」。所见即所得的前提是**所见的位置不变**:
 * 产物落回原来那个框,他接着改就是了。
 *
 * 于是这个组件有两态,共用同一个 textarea 位置:
 *   - 未润色:绑 `prompt`,底部是「✨ AI 润色」
 *   - 已整理:绑 `contentDescription`,顶部多一排识别到的信息点,
 *            底部换成「↩ 撤销这次整理」+「重新整理」,系统约束段折叠在下面(只读)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Loader2, RotateCw, Sparkles, Undo2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/button";
import { Textarea } from "@/shared/ui/textarea";
import { cn } from "@/shared/lib/utils";
import { PosterCard, PosterCardHeader } from "@/shared/ui/PosterCard";
import { POLISH_COST_LABEL } from "@/entities/scene-card/model/creditRates";
import { PROMPT_BOX_MAX } from "../../../model/free-create/buildRawPrompt";

const PROMPT_PLACEHOLDERS = [
  "例:双 11 全场五折,主推爆款保温杯。风格活力 + 红色渐变,突出限时三天",
  "例:春季招生季,瑜伽课程开放预约。淡绿色调,温暖治愈,留白多一点",
  "例:客户案例展示 — 某连锁烘焙店流水增长 230%。商务质感,数据可视化",
];

interface Props {
  prompt: string;
  setPrompt: (v: string) => void;
  polishing: boolean;
  onPolish: () => void;
  /** Wave 3 (2026-05-17) — Qwen 失败时的提示文案,非 null 时按钮 disabled + 红字提示。 */
  failureMessage?: string | null;
  /** 2026-05-25 — AI 优化按钮右侧的额外动作(自由创作改版:⚙ 参数 Popover)。 */
  paramsSlot?: React.ReactNode;
  /** 2026-05-25 — 标题/提示/占位覆盖(i2i「修改说明」复用本组件)。 */
  title?: string;
  hint?: string;
  placeholder?: string;
  /** 2026-05-25 — i2i 指令直送,隐藏 AI 优化按钮(只保留参数 + 字数)。 */
  hidePolish?: boolean;

  // ── 已整理态(2026-07-30 合框;这些都给了才切到那一态)────────────────────
  /** true = 展示归整产物(textarea 改绑 contentDescription)。 */
  isAiOptimized?: boolean;
  contentDescription?: string;
  setContentDescription?: (v: string) => void;
  /** 归整层给的只读系统约束段(折叠展示)。 */
  systemConstraints?: string;
  /** 识别到的信息点 —— 让用户看见"我们没漏东西"。 */
  coverageChips?: ReadonlyArray<{ category: string; label: string; count?: number }>;
  /**
   * 框内入口条 —— 产品 / 叠 logo 二维码 / 特别要求(2026-07-30)。
   *
   * ★为什么放进框里:它们是「这张图还要带什么」,与那段文案是同一件事。
   *   摆成页面上三个独立的段之后,用户写完文案就点生成了 —— 07-29 T1 的原话是
   *   「②③ 一次都没打开过」。做成框内 chip:不点不占版面,点了就地展开,
   *   设过值的 chip 变主色并写出当前值,「我设过什么」一眼可见。
   */
  entriesSlot?: React.ReactNode;
  /** 框内 handoff 一行(「这段内容更适合做一组图?」),只在写了字时由调用方给。 */
  handoffSlot?: React.ReactNode;
  /** 「一张装不下」的建议条 —— 只在内容真超载时由调用方给(见 overloadHint)。 */
  overloadSlot?: React.ReactNode;
  /**
   * 任务 19 —— 「这句像是排版要求,不是要印上去的字」那一问。
   * ★放在**框内、超载提示之上**:它说的是框里那段话本身,离得越近越看得懂。
   */
  metaHintSlot?: React.ReactNode;
  /**
   * 标题右侧的两态段控(从零写 / 改上传的图)。
   *
   * ★为什么挂在**框标题**上而不是页面顶部:它影响的就是这个框(写什么、框内出哪些入口),
   *   不是整页 —— 07-28 把它从页面级 tab 删掉的理由正是「改图是底图从哪来,做成页面级
   *   tab 会让半个页面的控件对它无意义」。挂在框上,作用域与视觉位置就对上了。
   * ★原来它是「保存草稿」旁边一行 13px 灰字链接,用户报的是「改图按钮要明显一些」。
   */
  modeSlot?: React.ReactNode;
  /**
   * 最终指令长度(buildRawPrompt 拼完的整串;2026-08-03)。给了就双口径显示:
   * 「文案 N · 最终指令 M/2000」—— 框内只报文案字数、提交闸却按整串判,
   * 两个口径同屏打架(用户实拍:1691/2000 绿着,提交报超 153 字)。
   */
  finalLen?: number;
  finalMax?: number;
  /** 撤销这次整理:回到原文。 */
  onRestoreOriginal?: () => void;
  /** 用原文重跑一次整理。 */
  onReOptimize?: () => void;
}

export function PromptPanel({
  prompt,
  setPrompt,
  polishing,
  onPolish,
  failureMessage,
  paramsSlot,
  title,
  hint,
  placeholder: placeholderProp,
  hidePolish = false,
  isAiOptimized = false,
  contentDescription = "",
  setContentDescription,
  systemConstraints = "",
  coverageChips = [],
  entriesSlot,
  handoffSlot,
  overloadSlot,
  metaHintSlot,
  modeSlot,
  finalLen,
  finalMax,
  onRestoreOriginal,
  onReOptimize,
}: Props) {
  const { t } = useTranslation("poster");
  const [showConstraints, setShowConstraints] = useState(false);
  /** 已整理态下这个框里的字就是产物;两态共用同一个位置(见文件头)。 */
  const optimized = isAiOptimized && !!setContentDescription;
  const value = optimized ? contentDescription : prompt;
  const onChangeValue = optimized ? setContentDescription! : setPrompt;
  // ───────── 自适应 textarea 高度 ─────────
  const taRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!taRef.current) return;
    taRef.current.style.height = "auto";
    taRef.current.style.height = `${Math.max(190, taRef.current.scrollHeight)}px`;
  }, [value]);

  const randomPlaceholder = useMemo(
    () => PROMPT_PLACEHOLDERS[Math.floor(Math.random() * PROMPT_PLACEHOLDERS.length)],
    []
  );
  const placeholder = placeholderProp ?? randomPlaceholder;

  return (
    // ★这一整张卡是页面主体:主色描边 + 阴影,一屏内只有它这么重。
    //   画风带已经去卡片化(见 StylePanel),右列是弱边框。
    <PosterCard className="border-primary/50 shadow-[0_4px_18px_rgba(37,99,235,0.10)]">
      <PosterCardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            {/* ★不复用 `PosterSectionTitle`:那是**段标题**规格(14px + 小圆点),
                而这一句是页面主标题 —— 用它就等于把主体降到段的层级,正是要修的那件事。 */}
            <h2 className="text-[19px] font-extrabold leading-tight tracking-[-0.2px]">
              {title ?? t("freeCreate.promptTitle")}
            </h2>
            <p className="mt-0.5 text-[12.5px] text-muted-foreground">
              {hint ?? t("freeCreate.promptHint")}
            </p>
          </div>
          {modeSlot}
        </div>
      </PosterCardHeader>
      <div className="px-5 pb-5">
        {/* 识别到的信息点 —— 「我们没漏东西」这件事要看得见(原来在另一个组件里) */}
        {optimized && coverageChips.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {coverageChips.map((c, i) => (
              <span
                key={`${c.category}-${i}`}
                className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[12px] font-medium text-emerald-600 dark:text-emerald-400"
              >
                {c.label}
              </span>
            ))}
          </div>
        )}
        <div className="relative rounded-xl border border-border/60 bg-background/40 transition-colors focus-within:border-primary/60">
          {/* V11 (2026-05-12) FREEFORM_RAW 走 rawPrompt;2026-09-09 老板拍板 2000→3000。
              上限常量在 buildRawPrompt.ts(PROMPT_BOX_MAX=框 / RAW_PROMPT_MAX=拼完总闸,
              与 ai 侧 max_length 同批改),别在这里写死第二份数。 */}
          <Textarea
            ref={taRef}
            value={value}
            onChange={(e) => onChangeValue(e.target.value)}
            placeholder={placeholder}
            rows={5}
            maxLength={PROMPT_BOX_MAX}
            className="min-h-[190px] resize-none border-0 bg-transparent p-4 pb-2 text-[14.5px] leading-relaxed focus-visible:ring-0 focus-visible:ring-offset-0"
          />
          {/* 框内入口条:产品 / 叠 logo 二维码 / 特别要求 */}
          {entriesSlot && <div className="px-3 pb-1">{entriesSlot}</div>}
          {metaHintSlot && <div className="px-3 pb-1.5">{metaHintSlot}</div>}
          {overloadSlot && <div className="px-3 pb-1.5">{overloadSlot}</div>}
          {handoffSlot && <div className="px-3 pb-1">{handoffSlot}</div>}
          <div className="flex items-center justify-between gap-2 px-3 pb-2.5 pt-1">
            <div className="flex items-center gap-1.5">
              {!hidePolish && !optimized && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 px-2 text-[13px] text-primary hover:bg-primary/10 cursor-pointer"
                  onClick={onPolish}
                  disabled={polishing || !prompt.trim() || failureMessage != null}
                >
                  {polishing ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  {/* 2026-07-30 曾从「AI 优化」改成「帮我把文案整理好」(嫌"优化"没说清优化的是什么);
                      2026-07-31 按用户口径定名 **「AI 润色」** —— 一句话按钮挤在画幅选择器左边,
                      短标签比整句更像个按钮,且"润色"本来就是这功能在产品里的老名字(handlePolish)。
                      2026-07-31 补价:归整层已开票,标的是 6 笔真跑实测的区间(约 55~70)。
                      ★2026-08-26 批 L:**它已经免费了** —— `ai.polish` / `poster.text_assist`
                      在 xb_price_sku 里是 pricing_mode=FREE(老板拍板的 6 类免费动作之二),
                      结算实扣 0。继续标「约 55~70 积分」是对着用户虚报一笔不存在的费用,
                      而且会劝退一个免费功能。 */}
                  {polishing ? "正在润色…" : "AI 润色"}
                  {!polishing && (
                    <span className="text-2xs font-normal text-muted-foreground">
                      {POLISH_COST_LABEL}
                    </span>
                  )}
                </Button>
              )}
              {optimized && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 px-2 text-[13px] text-muted-foreground hover:text-foreground cursor-pointer"
                    onClick={onRestoreOriginal}
                    disabled={polishing}
                  >
                    <Undo2 className="h-3 w-3" />
                    撤销这次整理
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 px-2 text-[13px] text-muted-foreground hover:text-foreground cursor-pointer"
                    onClick={onReOptimize}
                    disabled={polishing}
                  >
                    {polishing ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RotateCw className="h-3 w-3" />
                    )}
                    重新整理
                  </Button>
                </>
              )}
              {paramsSlot}
            </div>
            {finalLen != null && finalMax != null ? (
              <span
                className={cn(
                  "text-2xs tabular-nums",
                  finalLen >= finalMax * 0.9 ? "text-warning" : "text-muted-foreground",
                  finalLen > finalMax && "text-destructive font-medium"
                )}
                title="出图指令上限按「最终指令」算:画风/配色/字体/画幅/产品附加都计入,不只文案本身"
              >
                文案 {value.length} · 最终指令 {finalLen}/{finalMax} 字
                {finalLen > finalMax && ` · 超 ${finalLen - finalMax}`}
              </span>
            ) : (
              <span
                className={cn(
                  "text-2xs tabular-nums",
                  value.length >= PROMPT_BOX_MAX - 200 ? "text-warning" : "text-muted-foreground",
                  value.length >= PROMPT_BOX_MAX && "text-destructive font-medium"
                )}
              >
                {t("freeCreate.charCount", { cur: value.length })}
              </span>
            )}
          </div>
        </div>
        {/* 系统约束段:归整产物的一部分,**只读** —— 用户改的是上面那个框。
            它也会随提交发出去,所以必须能看到(不是可以省的装饰)。 */}
        {optimized && systemConstraints.trim() && (
          <div className="mt-2 rounded-lg border border-dashed border-border/70 bg-muted/20">
            <button
              type="button"
              onClick={() => setShowConstraints((v) => !v)}
              aria-expanded={showConstraints}
              className="flex w-full items-center gap-2 px-3 py-2 text-left"
            >
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
                  showConstraints && "rotate-180"
                )}
              />
              <span className="text-[12.5px] font-medium text-foreground">
                系统约束
                <span className="ml-1 font-normal text-muted-foreground">(只读,随这次一起发)</span>
              </span>
            </button>
            {showConstraints && (
              <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words px-3 pb-3 font-sans text-[12.5px] leading-relaxed text-muted-foreground">
                {systemConstraints}
              </pre>
            )}
          </div>
        )}
        {failureMessage ? (
          <p role="alert" className="mt-2 text-[13px] text-destructive">
            {failureMessage}
          </p>
        ) : null}
      </div>
    </PosterCard>
  );
}
